import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { timetables } from '../data/timetables';
import TopBar from '../components/TopBar';
import BusTable from '../components/BusTable';
import { useTimetableSocket } from '../hooks/useTimetableSocket';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';

const TimetableDetail = () => {
  const { id } = useParams();
  const { t } = useLanguage();
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const lastTap = useRef(0);
  const baseTimetable = useMemo(() => timetables.find(t => t.id === id), [id]);

  const get24HTime = () => {
    return new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  };

  const [currentTime, setCurrentTime] = useState(get24HTime());

  const activeRoute = useMemo(() => {
    if (!baseTimetable) return null;
    
    const now = new Date();
    const day = now.getDay();
    const hour = now.getHours();
    
    let variantToday = null;

    // Logic: 1 day before at 19:00
    if (baseTimetable.id === 'line10') {
      // Sat variant (6) starts Fri (5) at 19:00
      if ((day === 5 && hour >= 19) || day === 6) {
        variantToday = baseTimetable.variants[6];
      }
    } else if (baseTimetable.id === 'line20') {
      // Tue variant (2) starts Mon (1) at 19:00
      if ((day === 1 && hour >= 19) || day === 2) {
        variantToday = baseTimetable.variants[2];
      }
    }

    if (variantToday) {
      return {
        ...baseTimetable,
        activeId: variantToday.id,
        activeSocketId: variantToday.socketId,
        activeTitle: variantToday.title,
        isVariant: true,
        variantNote: t(variantToday.noteKey)
      };
    }

    return {
      ...baseTimetable,
      activeId: baseTimetable.id,
      activeSocketId: baseTimetable.socketId,
      activeTitle: baseTimetable.title,
      isVariant: false,
      variantNote: null
    };
  }, [baseTimetable, t]);

  const { data, status, isLoading } = useTimetableSocket(activeRoute?.activeSocketId);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(get24HTime());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  if (!baseTimetable) {
    return <Navigate to="/" replace />;
  }

  const API_KEY = import.meta.env.VITE_WSX_API_KEY;
  const mapUrl = `https://middleware2.wsx.gr/map/${activeRoute.activeId}?key=${API_KEY}&showLocation=true&geolocationZoom=14`;

  const handleMapInteraction = () => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      setIsMapFullscreen(true);
    }
    lastTap.current = now;
  };

  return (
    <div className="h-full bg-slate-900 flex flex-col overflow-hidden relative">
      <AnimatePresence>
        {isMapFullscreen && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="fixed inset-0 z-[100] bg-black flex flex-col">
            <div className="absolute top-4 right-4 z-[110] flex items-center space-x-2">
              <button onClick={() => setIsMapFullscreen(false)} className="bg-slate-800/90 backdrop-blur-md p-3 rounded-full shadow-2xl active:scale-90 transition-transform border border-white/10">
                <SafeIcon icon={FiIcons.FiMinimize2} className="text-xl text-white" />
              </button>
            </div>
            <div className="absolute top-4 inset-x-0 z-[110] flex justify-center pointer-events-none">
              <div className="bg-slate-900/80 backdrop-blur-md px-5 py-2 rounded-2xl border border-white/10 flex flex-col items-center pointer-events-auto">
                <div className="flex items-center space-x-2 mb-0.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${status === 'connected' ? 'bg-green-500 animate-pulse' : 'bg-orange-500'}`} />
                  <span className="text-white font-bold text-[10px] tracking-wider uppercase">
                    {status === 'connected' ? t('liveGps') : t('connecting')}
                  </span>
                </div>
                <div className="flex items-center text-[10px] font-mono text-white/50">
                  <SafeIcon icon={FiIcons.FiClock} className="mr-1 text-[8px]" />
                  {currentTime}
                </div>
              </div>
            </div>
            <iframe src={mapUrl} className="w-full h-full border-0" allow="geolocation" title={`${activeRoute.activeTitle} Fullscreen`} />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="shrink-0 flex flex-col z-20 bg-slate-900 shadow-sm border-b border-slate-800">
        <TopBar showBack={true}>
          <div className="flex items-center space-x-2 mt-0.5">
            <div className="flex items-center space-x-1.5">
              <div className={`w-1.5 h-1.5 rounded-full ${status === 'connected' ? 'bg-green-500 animate-pulse' : 'bg-orange-500'}`} />
              <span className="text-[9px] font-black uppercase tracking-wider text-slate-400 leading-none">
                {status === 'connected' ? t('liveGps') : t('connecting')}
              </span>
            </div>
            <div className="flex items-center text-[9px] font-mono text-slate-400">
              <SafeIcon icon={FiIcons.FiClock} className="mr-0.5 text-[8px]" />
              {currentTime}
            </div>
          </div>
        </TopBar>
        
        <div className="w-full bg-slate-800 border-b border-slate-800">
          <div className="relative w-full aspect-video md:aspect-[21/9] bg-slate-800 group cursor-pointer" onClick={handleMapInteraction}>
            <iframe src={mapUrl} className="absolute inset-0 w-full h-full border-0 pointer-events-auto" allow="geolocation" title={activeRoute.activeTitle} />
            <button onClick={(e) => { e.stopPropagation(); setIsMapFullscreen(true); }} className="absolute bottom-3 right-3 z-30 p-2.5 bg-slate-800/80 backdrop-blur-md rounded-xl shadow-lg border border-white/10 text-white active:scale-95 transition-all">
              <SafeIcon icon={FiIcons.FiMaximize} className="text-lg" />
            </button>
          </div>
        </div>

        <div className="px-4 md:px-8 py-4 bg-slate-900/50 backdrop-blur-sm border-b border-slate-800">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('arrivalTimes')}</h2>
            {activeRoute.isVariant && (
              <span className="text-[10px] font-bold bg-amber-900/40 text-amber-300 border border-amber-800/50 px-2 py-0.5 rounded-md">
                {activeRoute.variantNote}
              </span>
            )}
          </div>
          <p className="text-xl md:text-2xl font-extrabold text-white leading-tight">
            {activeRoute.activeTitle}
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 md:px-8 py-4 z-10 relative">
        <div className="max-w-5xl mx-auto">
          <BusTable rows={data} isLoading={isLoading} />
          <div className="mt-6 p-4 md:p-6 bg-slate-800 rounded-2xl border border-slate-700 flex items-start space-x-4">
            <div className="p-2 md:p-3 bg-slate-700 rounded-lg shrink-0">
              <SafeIcon icon={FiIcons.FiInfo} className="text-slate-300 text-lg" />
            </div>
            <div className="flex-1">
              <h4 className="text-xs md:text-sm font-bold text-white mb-0.5">{t('realTimeInfo')}</h4>
              <p className="text-[11px] md:text-xs text-slate-300 leading-relaxed font-medium">
                {t('infoDesc', { route: activeRoute.activeId })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimetableDetail;